/**
 * NoiseChart - per-case score variance for replicated runs.
 *
 * Shows each case's mean score with +/- 1 SD error bars, plus a shaded
 * "noise floor" band (height = MDE = 2x typical per-case SD) centred on the
 * overall mean. Cases whose SD exceeds the typical SD are highlighted as the
 * noisy ones. Mirrors experiment_analysis.py noise mode, computed client-side
 * from the replicate entries already in the run JSON.
 */

import React from "react";

function computeNoise(results) {
    const groups = {};
    (results.cases || []).forEach((c) => {
        const res = c.results || [];
        if (!res.length) return; // skip errored / ungraded entries
        const passed = res.filter((r) => r.result === "PASS").length;
        const score = passed / res.length;
        (groups[c.case_id] = groups[c.case_id] || []).push(score);
    });

    const perCase = Object.entries(groups)
        .map(([id, scores]) => {
            const n = scores.length;
            const mean = scores.reduce((a, b) => a + b, 0) / n;
            let sd = 0;
            if (n > 1) {
                const v = scores.reduce((a, b) => a + (b - mean) ** 2, 0) / (n - 1);
                sd = Math.sqrt(v);
            }
            return { id, n, mean, sd, min: Math.min(...scores), max: Math.max(...scores) };
        })
        .sort((a, b) => a.id.localeCompare(b.id));

    const sds = perCase.filter((c) => c.n > 1).map((c) => c.sd);
    const typicalSD = sds.length ? sds.reduce((a, b) => a + b, 0) / sds.length : 0;
    const mde = 2 * typicalSD;
    const overallMean = perCase.length
        ? perCase.reduce((a, c) => a + c.mean, 0) / perCase.length
        : 0;
    return { perCase, typicalSD, mde, overallMean };
}

const shortId = (id) => (id || "").replace("Standardized_Case_", "");

const NoiseChart = ({ results }) => {
    const replicates = results?.summary?.replicates ?? results?.config?.replicates ?? 1;
    const { perCase, typicalSD, mde, overallMean } = computeNoise(results);

    if (replicates < 2 || perCase.length === 0) {
        return (
            <div className="noise-chart-empty">
                Variance view needs replicates &ge; 2. Re-run with the <strong>Reps</strong> field
                set to 5&ndash;10 to see per-case noise.
            </div>
        );
    }

    // Layout
    const W = 900;
    const H = 380;
    const m = { top: 24, right: 24, bottom: 54, left: 48 };
    const plotW = W - m.left - m.right;
    const plotH = H - m.top - m.bottom;
    const y = (v) => m.top + plotH * (1 - v); // score 0..1 -> pixels
    const n = perCase.length;
    const band = plotW / n;
    const cx = (i) => m.left + band * (i + 0.5);

    const bandTop = y(Math.min(1, overallMean + mde / 2));
    const bandBot = y(Math.max(0, overallMean - mde / 2));

    return (
        <div className="noise-chart">
            <div className="noise-chart-header">
                <span>Per-case score variance ({replicates} replicates)</span>
                <span className="noise-chart-stats">
                    typical SD {typicalSD.toFixed(3)} &nbsp;|&nbsp; MDE {mde.toFixed(3)} &nbsp;|&nbsp;
                    overall mean {overallMean.toFixed(3)}
                </span>
            </div>
            <svg viewBox={`0 0 ${W} ${H}`} className="noise-svg" preserveAspectRatio="xMidYMid meet">
                {/* y gridlines */}
                {[0, 0.25, 0.5, 0.75, 1].map((v) => (
                    <g key={v}>
                        <line x1={m.left} y1={y(v)} x2={W - m.right} y2={y(v)} stroke="#333" strokeWidth="1" />
                        <text x={m.left - 8} y={y(v) + 4} textAnchor="end" fontSize="11" fill="#888">
                            {v.toFixed(2)}
                        </text>
                    </g>
                ))}

                {/* MDE noise-floor band around the overall mean */}
                <rect
                    x={m.left}
                    y={bandTop}
                    width={plotW}
                    height={Math.max(1, bandBot - bandTop)}
                    fill="rgba(149, 117, 205, 0.18)"
                />
                <line x1={m.left} y1={y(overallMean)} x2={W - m.right} y2={y(overallMean)}
                    stroke="#9575cd" strokeWidth="1" strokeDasharray="4 4" />
                <text x={W - m.right} y={bandTop - 4} textAnchor="end" fontSize="10" fill="#b39ddb">
                    noise floor (+/- MDE/2): differences inside this band are indistinguishable
                </text>

                {/* per-case mean + SD error bars */}
                {perCase.map((c, i) => {
                    const noisy = c.sd > typicalSD && typicalSD > 0;
                    const col = noisy ? "#ff9800" : "#64b5f6";
                    const hi = y(Math.min(1, c.mean + c.sd));
                    const lo = y(Math.max(0, c.mean - c.sd));
                    return (
                        <g key={c.id}>
                            {/* error bar */}
                            <line x1={cx(i)} y1={hi} x2={cx(i)} y2={lo} stroke={col} strokeWidth="2" />
                            <line x1={cx(i) - 5} y1={hi} x2={cx(i) + 5} y2={hi} stroke={col} strokeWidth="2" />
                            <line x1={cx(i) - 5} y1={lo} x2={cx(i) + 5} y2={lo} stroke={col} strokeWidth="2" />
                            {/* mean marker */}
                            <circle cx={cx(i)} cy={y(c.mean)} r="4" fill={col} />
                            {/* x label */}
                            <text x={cx(i)} y={H - m.bottom + 18} textAnchor="middle" fontSize="11" fill="#bbb">
                                {shortId(c.id)}
                            </text>
                            <text x={cx(i)} y={H - m.bottom + 32} textAnchor="middle" fontSize="9" fill="#777">
                                sd {c.sd.toFixed(2)}
                            </text>
                        </g>
                    );
                })}

                {/* axis labels */}
                <text x={m.left} y={H - 6} fontSize="11" fill="#888">case</text>
                <text x={14} y={m.top + plotH / 2} fontSize="11" fill="#888"
                    transform={`rotate(-90 14 ${m.top + plotH / 2})`} textAnchor="middle">
                    mean score +/- 1 SD
                </text>
            </svg>
            <div className="noise-chart-legend">
                <span><span className="swatch blue" /> stable (SD &le; typical)</span>
                <span><span className="swatch orange" /> noisy (SD &gt; typical)</span>
                <span><span className="swatch band" /> noise floor (MDE)</span>
            </div>
        </div>
    );
};

export default NoiseChart;
